import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import {
  MultiStepFormProvider,
  useMultiStepForm,
  type ServiceType,
  type ContactData,
} from "./MultiStepFormContext";
import { FormProgress } from "./FormProgress";
import {
  ServiceTypeStep,
  ProjectDetailsStep,
  ContactInfoStep,
  SuccessStep,
} from "./steps";

// Animation variants for step transitions
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 100 : -100,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 100 : -100,
    opacity: 0,
  }),
};

function FormContent() {
  const {
    currentStep,
    isFirstStep,
    isLastStep,
    isSubmitting,
    canProceed,
    nextStep,
    prevStep,
    submitForm,
  } = useMultiStepForm();

  const [direction, setDirection] = useState(0);
  const formRef = useRef<HTMLDivElement>(null);

  // Scroll to form top when changing steps
  const scrollToFormTop = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleNext = async () => {
    if (isLastStep) {
      await submitForm();
    } else {
      setDirection(1);
      nextStep();
      scrollToFormTop();
    }
  };

  const handleBack = () => {
    setDirection(-1);
    prevStep();
    scrollToFormTop();
  };

  const renderStep = () => {
    switch (currentStep) {
      case "service":
        return <ServiceTypeStep />;
      case "details":
        return <ProjectDetailsStep />;
      case "contact":
        return <ContactInfoStep />;
      case "success":
        return <SuccessStep />;
      default:
        return null;
    }
  };

  return (
    <div ref={formRef} className="w-full">
      <FormProgress />

      {/* Step content with animations */}
      <div className="min-h-[400px]">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation buttons - hide on success */}
      {currentStep !== "success" && (
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-[var(--dug-border)]">
          <button
            type="button"
            onClick={handleBack}
            disabled={isFirstStep}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all
              ${isFirstStep
                ? "opacity-0 cursor-default"
                : "text-[var(--dug-text-primary)] hover:bg-[var(--dug-bg-surface)]"
              }
            `}
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <button
            type="button"
            onClick={handleNext}
            disabled={!canProceed() || isSubmitting}
            className={`
              dugout-primary flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all
              ${canProceed() && !isSubmitting
                ? "bg-[var(--dug-coral)] text-[var(--dug-text-inverse)] hover:opacity-90"
                : "bg-[var(--dug-border)] text-[var(--dug-text-muted)] cursor-not-allowed"
              }
            `}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : isLastStep ? (
              <>
                Submit
                <ArrowRight className="w-4 h-4" />
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

interface MultiStepFormProps {
  onSubmit: (data: {
    serviceType: ServiceType;
    answers: Record<string, unknown>;
    contact: ContactData;
  }) => Promise<boolean>;
}

export function MultiStepForm({ onSubmit }: MultiStepFormProps) {
  return (
    <MultiStepFormProvider onSubmit={onSubmit}>
      <FormContent />
    </MultiStepFormProvider>
  );
}
